import { GraphQL } from '@guestgoodie/graphql'

export type FormTranslations = Partial<
  Record<GraphQL.AppLocale, Record<string, string>>
>

/**
 * Fills a value into a nested object structure based on a dot-notation field path.
 *
 * This function uses **object references** to traverse and mutate the target object.
 * As we move through each level of the path, we reassign `objRef` to point to the
 * next nested level. This allows us to modify the original `obj` parameter directly
 * without needing to return or merge nested structures.
 *
 * Key behavior:
 * - Converts snake_case field names to camelCase before processing
 * - Creates intermediate objects/arrays as needed during traversal
 * - Supports array indices in the format `[0]`, `[1]`, etc.
 * - Supports empty array notation `[]` which creates an empty array and stops
 * - **Mutates the input object** - does not create a copy
 *
 * @param obj - The target object to fill into (mutated in place)
 * @param field - Dot-notation path in snake_case (e.g., "default.request_fields.[0].label")
 * @param value - The value to set at the path location
 *
 * @returns The mutated input object (for chaining convenience)
 *
 * @example
 * const obj = {}
 * fillIntoObject(obj, "default.request_fields.[0].label", "Test")
 * // obj is now: { default: { requestFields: [{ label: "Test" }] } }
 *
 * @example
 * const obj = {}
 * fillIntoObject(obj, "settings.colors.[]", null)
 * // obj is now: { settings: { colors: [] } }
 *
 * How reference traversal works:
 * 1. Start with objRef pointing to the root object
 * 2. For each path segment:
 *    - Check if we need to create an array or object at this level
 *    - Reassign objRef to point to the next nested level
 * 3. At the final segment, set the value directly on objRef
 * 4. Because objRef is always pointing to nested parts of the original obj,
 *    all modifications affect the original object
 */
function fillIntoObject(
  obj: Record<string, unknown>,
  field: string,
  value: string | null
) {
  const splitField = snakeToCamelCase(field).split('.')
  const length = splitField.length
  const lastElementIndex = length - 1

  // objRef is a reference that walks through the nested object structure.
  // We reassign it at each level to point deeper into the object tree.
  // This allows us to mutate the original `obj` as we traverse.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let objRef: any = obj

  for (let index = 0; index < length; index++) {
    let field: string | number = splitField[index]
    const nextField = splitField[index + 1]
    const isEnd = lastElementIndex === index

    // Extract the numeric index from array notation like "[0]" -> "0"
    const indexMatch = field.match(/^\[([0-9]+)\]$/)?.[1]
    // Check if the next field is also an array index
    const isNextArrayIndex = nextField?.match(/^\[([0-9]+)\]$/)?.[1] != null

    // Convert string index to number for array access
    if (indexMatch != null) {
      field = parseInt(indexMatch)
    }

    if (nextField === '[]') {
      // Special case: empty array notation
      objRef[field] = []
      break
    } else if (isEnd) {
      // We've reached the end - set the value
      objRef[field] = value
    } else if (objRef[field] == null) {
      // Need to create intermediate structure - array or object?
      if (isNextArrayIndex) objRef[field] = []
      else objRef[field] = {}
    }

    // Move the reference one level deeper into the object tree
    // This is the key: objRef now points to a nested part of the original obj
    objRef = objRef[field]
  }

  return obj
}

type GraphqlToFormOptions = {
  prefix?: string
}

export function graphqlToForm(
  translations: (GraphQL.TranslationFragmentFragment | null)[],
  fields: string[],
  locales: GraphQL.AppLocale[],
  opts: GraphqlToFormOptions = {}
) {
  // We have to build a base else react-hook-form will need two saves before it recognizes fields correctly as not dirty
  // so we need to set all possible fields here to null initally (except for arrays, which will just be empty always)
  const base = Object.fromEntries(
    locales.map(locale => [
      locale,
      fields.reduce((acc, field) => {
        const finalField = opts.prefix ? `${opts.prefix}${field}` : field

        return fillIntoObject(acc, finalField, null)
      }, {})
    ])
  )

  return translations.reduce<FormTranslations>(function (acc, trans) {
    if (!trans) return acc

    const locale = trans.locale as GraphQL.AppLocale

    if (!(trans.locale in acc)) acc[locale] = {}

    if (locale in acc && !!acc[locale]) {
      const finalField = opts.prefix
        ? `${opts.prefix}${trans.field}`
        : trans.field

      fillIntoObject(acc[locale], finalField, trans.value)
    }

    return acc
  }, base)
}

type FormToGraphqlOptions = {
  prefixToRemove?: string
  DEBUG?: boolean
}

export function formToGraphql(
  translations: FormTranslations,
  opts?: FormToGraphqlOptions
) {
  if (opts?.DEBUG) console.group('formToGraphQL DEBUG')

  const result = Object.entries(translations).flatMap(function ([
    locale,
    loc_translations
  ]) {
    return mapFormValue(locale as GraphQL.AppLocale, loc_translations, opts)
  })

  if (opts?.DEBUG) console.groupEnd()

  return result
}

function mapFormValue(
  locale: GraphQL.AppLocale,
  translations: object,
  opts: FormToGraphqlOptions = {},
  prefix?: string
): {
  value: string
  locale: GraphQL.AppLocale
  field: string
}[] {
  if (opts.DEBUG) console.log('Recursion', locale, translations)

  // It is possible that we have an array of strings as the last field, then we would get a string for the translations value here.
  // For example the options in a RequestField
  if (typeof translations === 'string' && prefix) {
    if (opts.DEBUG) console.log('Translations is a string: ', translations)

    return [
      {
        value: translations,
        locale,
        field: prefix
      }
    ]
  }

  return Object.entries(translations).flatMap(function ([key, value]) {
    if (opts.DEBUG) console.log(key, value)

    if (!value) return []

    const elixirField = camelToSnakeCase(key)
    const field = prefix ? `${prefix}.${elixirField}` : elixirField

    if (opts.DEBUG) console.log('First Field', field)

    const finalField =
      opts.prefixToRemove && field.startsWith(opts.prefixToRemove)
        ? field.replace(opts.prefixToRemove, '')
        : field

    if (opts.DEBUG) console.log('FINAL Field', finalField)

    if (Array.isArray(value)) {
      return value.flatMap((val, idx) =>
        mapFormValue(locale, val, opts, `${finalField}.[${idx}]`)
      )
    }

    if (typeof value === 'object') {
      return mapFormValue(locale, value, opts, `${finalField}`)
    }

    return {
      value,
      locale,
      field: finalField
    }
  })
}

// We have to manually convert between camelCase and snakeCase with elixir
function camelToSnakeCase(str: string) {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

function snakeToCamelCase(str: string) {
  return str.replace(/_[a-z]/g, letter => letter[1].toUpperCase())
}
